import {
  extractCheckoutErrorMessage,
  extractTransactionToken,
  loadCheckoutScript,
  parseCheckoutAmount,
} from './bbmsCheckout'

// Blackbaud's default zone when an organization has not been told otherwise.
// Luminate's `getDonationFormInfo` response reports the org's actual zone.
const DEFAULT_ZONE = 'p-usa01'

// How long to wait for any sign of life from Blackbaud's iframe before deciding the mount
// genuinely failed. When it fails there is no ready and no error, so without this a donor
// would watch a spinner forever.
const READY_TIMEOUT_MS = 20000

/**
 * Inline (embedded) Blackbaud Checkout controller.
 *
 * Unlike the modal workflow in `bbmsCheckout.js`, the inline workflow renders
 * Blackbaud's card fields directly inside our donation form and takes over our
 * submit button. Authorization is triggered by the donor clicking that button,
 * so the host form has to keep Blackbaud informed of two things: the current
 * transaction data (amount, billing name) and whether our own fields are valid
 * enough for the button to be clickable.
 *
 * The resulting transaction token is handed back through `onToken` and is
 * interchangeable with the one the modal workflow produces — both are posted to
 * Luminate's `donateCheckout` by our Django proxy.
 *
 * @param {Object} params
 * @param {string} params.environmentId          - Luminate Online environmentId.
 * @param {string} params.paymentConfigurationId - Luminate Online paymentConfigurationId.
 * @param {string} [params.zone]                 - Blackbaud zone, e.g. p-usa01.
 * @param {Function} [params.onReady]            - Called once with (true, availablePaymentModes)
 *                                                 when the fields are usable. Never called with false.
 * @param {Function} [params.onValidChange]      - Called with Blackbaud's own field validity.
 * @param {Function} [params.onLoadingChange]    - Called while Blackbaud saves payment details.
 * @param {Function} [params.onAuthorizationChange] - Called while authorization is in flight.
 * @param {Function} [params.onClicked]          - Called on every button click, enabled or not.
 * @param {Function} [params.onToken]            - Called with the transaction token on success.
 * @param {Function} [params.onError]            - Called with a donor-facing error message.
 */
export function createInlineCheckout({
  environmentId,
  paymentConfigurationId,
  zone,
  onReady,
  onValidChange,
  onLoadingChange,
  onAuthorizationChange,
  onClicked,
  onToken,
  onError,
}) {
  let checkoutService = null
  let subscriptions = []
  let latestTransactionId = ''
  let destroyed = false
  let mounted = false
  let readyTimer = null
  let readySignalled = false
  let inlineMountId = ''

  const clearReadyTimer = () => {
    if (readyTimer) clearTimeout(readyTimer)
    readyTimer = null
  }

  // Blackbaud's `ready` observable does not fire on every successful mount — an inline
  // checkout can render its card fields, accept input and emit `valid` while `ready` stays
  // silent. So treat any sign of life from the iframe as readiness, and latch it, since
  // readiness is not something the SDK takes back once the fields are up.
  const signalReady = (availablePaymentModes) => {
    if (readySignalled || destroyed) return
    readySignalled = true
    clearReadyTimer()
    onReady?.(true, availablePaymentModes)
  }

  const hasMountedIframe = () =>
    !!(inlineMountId && document.getElementById(inlineMountId)?.querySelector('iframe'))

  const emitError = (message) => {
    if (destroyed) return
    onError?.(message || 'Payment could not be processed. Please try again.')
  }

  const handleCompletion = (payload) => {
    if (destroyed) return
    const token = extractTransactionToken(payload, checkoutService, latestTransactionId)
    const looksSuccessful = payload?.success !== false

    if (looksSuccessful && token) {
      onToken?.(token)
      return
    }
    if (looksSuccessful && !token) {
      // Some Blackbaud environments fire the completion event before the
      // transaction id arrives over postMessage. Give it a moment to land.
      setTimeout(() => {
        if (destroyed) return
        const lateToken = extractTransactionToken(payload, checkoutService, latestTransactionId)
        if (lateToken) return onToken?.(lateToken)
        emitError(
          extractCheckoutErrorMessage(payload) || 'Checkout completed without a transaction token.',
        )
      }, 750)
      return
    }
    emitError(
      extractCheckoutErrorMessage(payload) || 'Payment was declined or could not be processed.',
    )
  }

  const handleCheckoutMessage = (event) => {
    if (destroyed || !event?.data?.messageType) return
    const messageType = event.data.messageType
    const details = event.data.details || {}

    if (messageType === 'bbcheckout-transaction-id-change') {
      if (details.id) latestTransactionId = String(details.id).trim()
      return
    }
    if (messageType !== 'bbcheckout-checkout-complete') return
    if (details.success === false) {
      emitError(extractCheckoutErrorMessage(details))
      return
    }
    const token = extractTransactionToken(details, checkoutService, latestTransactionId)
    if (token) onToken?.(token)
  }

  const subscribe = (observable, handler) => {
    const sub = observable?.subscribe?.(handler)
    if (sub) subscriptions.push(sub)
  }

  /**
   * Loads the SDK and mounts Blackbaud's elements into the host form.
   *
   * @param {Object} targets
   * @param {string} targets.inlineElementId - Element that receives the card fields.
   * @param {string} targets.buttonElementId - Wrapper element that receives the click handler.
   * @param {string} targets.buttonName      - Name of our submit button, nested inside the wrapper.
   */
  const mount = async ({ inlineElementId, buttonElementId, buttonName }) => {
    if (mounted || destroyed) return
    if (!paymentConfigurationId) {
      throw new Error(
        'Blackbaud Checkout is not configured: missing paymentConfigurationId on the payment provider.',
      )
    }

    await loadCheckoutScript()
    if (destroyed) return

    if (typeof window.BlackbaudCheckout !== 'function') {
      throw new Error('Blackbaud Checkout SDK loaded but window.BlackbaudCheckout is unavailable.')
    }

    checkoutService = window.BlackbaudCheckout({
      environmentId,
      paymentConfigurationId,
      zone: zone || DEFAULT_ZONE,
      applicationName: 'Luminate',
      workflowMode: 'inline',
      languageLocale: 'en_US',
      feeOffsetType: 0,
      paymentMethodOptions: {
        wallets: {
          applePayEnabled: false,
          googlePayEnabled: false,
        },
      },
    })

    subscribe(checkoutService.transactionIdChanges, (id) => {
      if (id != null && String(id).trim() !== '') latestTransactionId = String(id).trim()
    })
    subscribe(checkoutService.inlineCheckoutComponent?.valid, (e) => {
      // A validity emission proves the fields are mounted and talking to us, whether or
      // not `ready` ever arrives.
      signalReady()
      onValidChange?.(!!e?.valid)
    })
    subscribe(checkoutService.inlineCheckoutComponent?.ready, (e) => {
      if (e?.ready) signalReady(e.availablePaymentModes)
    })
    subscribe(checkoutService.inlineCheckoutComponent?.loading, (e) =>
      onLoadingChange?.(!!e?.loading),
    )
    subscribe(checkoutService.completeCheckoutButtonComponent?.authorizationInProgress, (e) =>
      onAuthorizationChange?.(!!e),
    )
    subscribe(checkoutService.completeCheckoutButtonComponent?.clicked, () => onClicked?.())
    subscribe(checkoutService.checkoutComplete, handleCompletion)

    window.addEventListener('message', handleCheckoutMessage)

    inlineMountId = inlineElementId
    checkoutService.inlineCheckoutComponent.mount(inlineElementId)
    checkoutService.completeCheckoutButtonComponent.mount(buttonElementId, buttonName)
    mounted = true

    readyTimer = setTimeout(() => {
      readyTimer = null
      if (destroyed || readySignalled) return

      // Fields on screen but no events: a working checkout that simply never announced
      // itself. Let the donor pay rather than covering live fields with a failure message.
      if (hasMountedIframe()) {
        console.warn(
          'Blackbaud Checkout mounted its iframe but emitted neither ready nor valid. ' +
            'Treating the checkout as usable.',
        )
        signalReady()
        return
      }

      console.error(
        'Blackbaud Checkout never mounted an iframe. Check that environmentId, ' +
          'paymentConfigurationId and zone belong to the same organization. Note that a 401 ' +
          'from sts.sky.blackbaud.com with origin app.blackbaud.com is Blackbaud fetching ' +
          'theme preferences for a signed-in staff user and is expected for anonymous donors.',
        { environmentId, paymentConfigurationId, zone: zone || DEFAULT_ZONE },
      )
      emitError('The secure payment fields could not be loaded. Please refresh and try again.')
    }, READY_TIMEOUT_MS)
  }

  /** Keep Blackbaud's transaction amount in sync with the form. Dollars in, cents out. */
  const setAmount = (amount) => {
    if (!checkoutService) return
    const dollars = parseCheckoutAmount(amount)
    if (!Number.isFinite(dollars) || dollars <= 0) return
    checkoutService.baseAmount = Math.round(dollars * 100)
  }

  const setRecurring = (isRecurring) => {
    if (!checkoutService) return
    checkoutService.isRecurring = !!isRecurring
  }

  /** Card payments don't require a name, so the host form supplies one for the receipt. */
  const setBillingName = (name) => {
    if (!checkoutService || !name) return
    checkoutService.billingDetails = { name }
  }

  /** Controls whether clicking the mounted button starts authorization. */
  const setEnabled = (enabled) => {
    if (!checkoutService?.completeCheckoutButtonComponent) return
    checkoutService.completeCheckoutButtonComponent.enabled = !!enabled
  }

  const destroy = () => {
    destroyed = true
    mounted = false
    readySignalled = false
    inlineMountId = ''
    clearReadyTimer()
    window.removeEventListener('message', handleCheckoutMessage)
    subscriptions.forEach((sub) => {
      try {
        sub.unsubscribe?.()
      } catch (_) {
        // ignore unsubscribe errors
      }
    })
    subscriptions = []
    checkoutService = null
  }

  return { mount, setAmount, setRecurring, setBillingName, setEnabled, destroy }
}
